import { Box, Stack, Typography, Divider } from "@mui/material";
import {
  AccessTime,
  EventAvailable,
  Cancel,
  MoneyOff,
  Warning,
} from "@mui/icons-material";
import useDocumentMeta from "../../utils/useDocumentMeta";

const CancellationPolicy = () => {
  useDocumentMeta(
    "Cancellation Policy | Radiance Aesthetics | Hindley | Wigan",
    "Booking fee, rescheduling and cancellation policy for Radiance Aesthetics, Hindley, Wigan — what to expect if you need to change or cancel your appointment.",
  );

  const policyItems = [
    {
      icon: (
        <MoneyOff sx={{ color: "var(--color-text-muted)", fontSize: 20 }} />
      ),
      title: "Deposit Requirement",
      content:
        "All appointments require a £20 booking fee. This covers admin and all preparatory work carried out before the service takes place. Bookings will not be secured until the fee has been paid, and fees are strictly non-refundable.",
    },
    {
      icon: <EventAvailable sx={{ color: "#4caf50", fontSize: 20 }} />,
      title: "Free Rescheduling (48+ hours notice)",
      content:
        "Booking fees are deducted from the treatment cost. Should you need to rearrange your appointment, and have provided more than 48 hours notice, you can do so free of charge and the original fee will be carried over.",
    },
    {
      icon: <AccessTime sx={{ color: "#ff9800", fontSize: 20 }} />,
      title: "Late Rescheduling (Less than 48 hours)",
      content:
        "If you need to rearrange your appointment and less than 48 hours notice has been given, your £20 booking fee will be lost and a further £20 fee will be required to secure your next booking.",
    },
    {
      icon: <Cancel sx={{ color: "#f44336", fontSize: 20 }} />,
      title: "Last Minute Changes (Less than 24 hours)",
      content:
        "If you need to cancel or rearrange your appointment with less that 24 hours notice, you will be required to pay a £35 cancellation fee, in addition to your pre-paid booking fee.",
    },
    {
      icon: <Warning sx={{ color: "#d32f2f", fontSize: 20 }} />,
      title: "No-Show Policy",
      content:
        "If you do not turn up to your appointment, or fail to inform us that you will not be attending, you will be required to pay the full cost of treatment that you had booked in for.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: "var(--color-bg-warm)",
        padding: { xs: "40px 16px", md: "64px 24px" },
        minHeight: "60vh",
      }}
    >
      <Stack
        sx={{
          maxWidth: "720px",
          margin: "0 auto",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            color: "var(--color-accent)",
            textAlign: "center",
            mb: 1.5,
          }}
        >
          Policies
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-heading)",
            color: "var(--color-primary)",
            fontWeight: 400,
            textAlign: "center",
            fontSize: { xs: "28px", md: "40px" },
            mb: 1.5,
          }}
          variant="h1"
        >
          Cancellation Policy
        </Typography>

        <Typography
          sx={{
            color: "var(--color-text-light)",
            textAlign: "center",
            fontSize: "16px",
            mb: 5,
            maxWidth: "600px",
            lineHeight: 1.7,
            fontFamily: "var(--font-body)",
          }}
        >
          Please review our cancellation and rescheduling policies below.
        </Typography>

        <Stack
          gap={0}
          sx={{
            width: "100%",
            backgroundColor: "var(--color-white)",
            borderRadius: "8px",
            border: "1px solid var(--color-border)",
            overflow: "hidden",
          }}
        >
          {policyItems.map((item, index) => (
            <Box key={index}>
              <Stack
                direction="row"
                gap={2}
                sx={{
                  alignItems: "flex-start",
                  p: { xs: 2.5, md: 3 },
                }}
              >
                <Box sx={{ mt: 0.25, flexShrink: 0 }}>{item.icon}</Box>
                <Stack gap={0.75} sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "var(--color-primary)",
                      fontFamily: "var(--font-body)",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      lineHeight: 1.7,
                      color: "var(--color-text-light)",
                      fontFamily: "var(--font-body)",
                    }}
                  >
                    {item.content}
                  </Typography>
                </Stack>
              </Stack>
              {index < policyItems.length - 1 && (
                <Divider sx={{ opacity: 0.5 }} />
              )}
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 4,
            p: 2,
            backgroundColor: "var(--color-white)",
            borderRadius: "8px",
            width: "100%",
            border: "1px solid var(--color-border)",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "var(--color-text-muted)",
              fontStyle: "italic",
              textAlign: "center",
              fontFamily: "var(--font-body)",
            }}
          >
            We appreciate your understanding. These policies help us provide the
            best service to all our clients.
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default CancellationPolicy;
